import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  collectionGroup,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/collavue/PageHeader";

export default function AdminPage() {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tx, setTx] = useState([]);
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    if (!userProfile?.isAdmin) return;
    const subs = [];
    const safe = (q, set) => {
      try {
        const u = onSnapshot(
          q,
          (snap) => set(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => setErrors(err.message)
        );
        subs.push(u);
      } catch (e) {
        setErrors(e.message);
      }
    };
    safe(query(collection(db, "users"), orderBy("createdAt", "desc"), limit(40)), setUsers);
    safe(query(collectionGroup(db, "campaigns"), orderBy("createdAt", "desc"), limit(40)), setCampaigns);
    safe(query(collectionGroup(db, "syncJobs"), orderBy("startedAt", "desc"), limit(40)), setJobs);
    safe(
      query(
        collectionGroup(db, "creditTransactions"),
        orderBy("createdAt", "desc"),
        limit(40)
      ),
      setTx
    );
    return () => subs.forEach((u) => u && u());
  }, [userProfile]);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <PageHeader
        title="Admin"
        subtitle="Read-only view across users, campaigns, sync jobs, and credits."
      />
      <div className="mb-4 inline-flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
        <ShieldCheck className="w-3.5 h-3.5" /> Admin access enabled
      </div>

      {errors && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors} — make sure your Firestore rules grant admins read access.
        </div>
      )}

      <Section title={`Users (${users.length})`}>
        <Table
          columns={["Email", "Plan", "Credits", "Verified", "Created"]}
          rows={users.map((u) => [
            u.email,
            u.planTier || "free",
            u.credits ?? 0,
            u.isVerified ? "yes" : "no",
            u.createdAt?.toDate ? u.createdAt.toDate().toLocaleDateString() : "—",
          ])}
        />
      </Section>

      <Section title={`Recent campaigns (${campaigns.length})`}>
        <Table
          columns={["Name", "Company", "Industry", "Window", "Last sync"]}
          rows={campaigns.map((c) => [
            c.name,
            c.companyName,
            c.industry || "—",
            `${c.dateRangeDays || 7}d`,
            c.lastSyncAt?.toDate ? c.lastSyncAt.toDate().toLocaleString() : "—",
          ])}
        />
      </Section>

      <Section title={`Sync jobs (${jobs.length})`}>
        <Table
          columns={["Status", "Posts", "Leads", "Hot", "Started"]}
          rows={jobs.map((j) => [
            j.status,
            j.stats?.postsFetched || 0,
            j.stats?.leadsCreated || 0,
            j.stats?.hotLeads || 0,
            j.startedAt?.toDate ? j.startedAt.toDate().toLocaleString() : "—",
          ])}
        />
      </Section>

      <Section title={`Credit transactions (${tx.length})`}>
        <Table
          columns={["Reason", "Type", "Amount", "Balance", "When"]}
          rows={tx.map((t) => [
            t.reason,
            t.type,
            t.amount,
            t.balanceAfter,
            t.createdAt?.toDate ? t.createdAt.toDate().toLocaleString() : "—",
          ])}
        />
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
      <h2 className="font-semibold mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Table({ columns, rows }) {
  if (!rows.length)
    return <p className="text-sm text-slate-500">No records.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase font-semibold text-slate-500 border-b border-slate-200">
            {columns.map((c) => (
              <th key={c} className="py-2 pr-3">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} className="py-2 pr-3">
                  {String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
